import {
  LuAlarmSmoke,
  LuBatteryCharging,
  LuCable,
  LuFan,
  LuLightbulb,
  LuPhone,
  LuPlug,
  LuShield,
  LuSquareActivity,
  LuWrench,
  LuZap,
} from "react-icons/lu";

import { business, services } from "../content/site";

/**
 * Maps the lucide icon NAME carried by each service in src/content/site.js to
 * the matching react-icons component. The content file stays free of imports,
 * and an unrecognised or newly added name degrades to a sensible default
 * instead of crashing the render.
 *
 * Zero emoji. Icons are decorative only and are always aria-hidden — every
 * card's meaning is carried by its heading and body text.
 */
const SERVICE_ICONS = {
  wrench: LuWrench,
  "square-activity": LuSquareActivity,
  cable: LuCable,
  plug: LuPlug,
  "battery-charging": LuBatteryCharging,
  shield: LuShield,
  "alarm-smoke": LuAlarmSmoke,
  lightbulb: LuLightbulb,
  fan: LuFan,
};

const FALLBACK_ICON = LuZap;

function iconFor(name) {
  return SERVICE_ICONS[name] || FALLBACK_ICON;
}

/**
 * The call action that closes every card. Services have no detail routes on
 * this site, so linking to /services/:slug would produce dead links. The phone
 * number is the real conversion path and comes only from site.js.
 */
function CallAction({ label, className = "" }) {
  return (
    <a
      href={business.phone.href}
      className={`relative inline-flex items-center gap-2 min-h-tap text-body-sm
                  font-semibold text-ink-brand underline underline-offset-4 decoration-1
                  hover:text-[color:var(--color-action-primary-active)]
                  hover:decoration-2
                  active:text-[color:var(--color-action-primary-active)]
                  transition-colors duration-base ease-standard ${className}`}
    >
      <LuPhone aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="min-w-0">
        {label}
        <span className="sr-only">{` — call ${business.phone.display}`}</span>
      </span>
    </a>
  );
}

/**
 * Lead card. Larger type, icon plate, description, and — where the service
 * carries a `signs` array — the objection-handling list a worried homeowner
 * actually needs before picking up the phone.
 */
function FeaturedServiceCard({ service, headingId, wide }) {
  const Icon = iconFor(service.icon);
  const signsId = `${service.slug}-signs`;

  return (
    <article
      id={service.slug}
      aria-labelledby={headingId}
      className={`card flex h-full min-w-0 flex-col gap-4 p-6 sm:p-7
                  border-line hover:border-line-strong hover:shadow-md
                  focus-within:border-line-strong focus-within:shadow-md
                  transition-[box-shadow,border-color] duration-base ease-standard
                  ${wide ? "sm:col-span-2" : ""}`}
    >
      <span
        aria-hidden="true"
        className="inline-flex h-12 w-12 shrink-0 items-center justify-center
                   rounded-md bg-surface-brand-soft text-ink-brand"
      >
        <Icon className="h-6 w-6" />
      </span>

      <h3
        id={headingId}
        className="text-heading text-ink [overflow-wrap:anywhere]"
      >
        {service.title}
      </h3>

      <p className="max-w-prose text-body text-ink-muted">{service.short}</p>

      {service.signs && service.signs.length > 0 && (
        <div className="rounded-md bg-surface-sunken p-4">
          <p
            id={signsId}
            className="text-label uppercase text-ink-muted"
          >
            Call us if you notice
          </p>
          <ul
            aria-labelledby={signsId}
            className="mt-3 space-y-2 text-body-sm text-ink"
          >
            {service.signs.map((sign) => (
              <li key={sign} className="flex items-start gap-2">
                <span
                  aria-hidden="true"
                  className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-pill bg-urgent"
                />
                <span className="min-w-0 [overflow-wrap:anywhere]">{sign}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto pt-1">
        <CallAction label="Talk to an electrician" />
      </div>
    </article>
  );
}

/** Compact tile for the long tail. Same information model, quieter voice. */
function SecondaryServiceCard({ service, headingId }) {
  const Icon = iconFor(service.icon);

  return (
    <article
      id={service.slug}
      aria-labelledby={headingId}
      className="flex h-full min-w-0 flex-col gap-3 rounded-lg border border-line
                 bg-surface p-5
                 hover:border-line-strong hover:shadow-sm
                 focus-within:border-line-strong focus-within:shadow-sm
                 transition-[box-shadow,border-color] duration-base ease-standard"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center
                     rounded-md bg-surface-sunken text-ink-brand"
        >
          <Icon className="h-5 w-5" />
        </span>
        <h4
          id={headingId}
          className="min-w-0 text-body-lg font-semibold text-ink [overflow-wrap:anywhere]"
        >
          {service.title}
        </h4>
      </div>

      <p className="max-w-prose text-body-sm text-ink-muted">{service.short}</p>

      <div className="mt-auto pt-1">
        <CallAction label="Ask about this" />
      </div>
    </article>
  );
}

const Services = () => {
  const featured = services.filter((service) => service.featured);
  const secondary = services.filter((service) => !service.featured);

  return (
    <section
      id="services"
      aria-labelledby="services-heading"
      className="section bg-surface-sunken"
    >
      <div className="container-page">
        <header className="max-w-prose">
          <p className="kicker">What we do</p>
          <h2
            id="services-heading"
            className="mt-3 text-display text-ink"
          >
            Electrical work for homes and businesses
          </h2>
          <p className="mt-4 text-body-lg text-ink-muted">
            Start with whatever is happening at your place. If none of these
            quite describes it, call and tell us what you are seeing — we will
            point you to the right fix.
          </p>
        </header>

        {/* Lead group. The first card spans two tracks so the eye has a
            landing point instead of nine identical tiles. */}
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {featured.map((service, index) => (
            <FeaturedServiceCard
              key={service.slug}
              service={service}
              headingId={`${service.slug}-title`}
              wide={index === 0}
            />
          ))}
        </div>

        {secondary.length > 0 && (
          <div className="mt-12 border-t border-line pt-10">
            <h3
              id="services-more-heading"
              className="text-heading text-ink"
            >
              Also on the truck
            </h3>
            <p className="mt-2 max-w-prose text-body text-ink-muted">
              Smaller jobs we take on the same visit, or book on their own.
            </p>

            <div
              aria-labelledby="services-more-heading"
              className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {secondary.map((service) => (
                <SecondaryServiceCard
                  key={service.slug}
                  service={service}
                  headingId={`${service.slug}-title`}
                />
              ))}
            </div>
          </div>
        )}

        <div
          className="mt-12 flex flex-col items-start gap-4 rounded-lg
                     bg-surface p-6 shadow-sm sm:flex-row sm:items-center
                     sm:justify-between"
        >
          <p className="max-w-prose text-body text-ink">
            Not sure which one you need? Describe the problem and we will tell
            you what it takes to fix it.
          </p>
          <a
            href={business.phone.href}
            className="btn-urgent w-full shrink-0 sm:w-auto"
          >
            <LuPhone aria-hidden="true" className="h-5 w-5 shrink-0" />
            <span className="tabular">Call {business.phone.display}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
